import { useId, useState } from 'react';
import { useTranslation } from '@doccareplus/i18n';
import { SearchSelect } from '@doccareplus/ui';

interface Props {
  valueMinutes: number;
  onChange: (minutes: number) => void;
}

const PRESET_DURATIONS = [5, 10, 15, 30, 60] as const;

export function AppointmentDurationField({ valueMinutes, onChange }: Props) {
  const { t } = useTranslation('appointments');
  const customDurationLabelId = useId();
  const initialIsPreset = PRESET_DURATIONS.some((minutes) => minutes === valueMinutes);
  const [selection, setSelection] = useState<string | null>(
    valueMinutes > 0 ? (initialIsPreset ? String(valueMinutes) : 'custom') : null,
  );
  const [customHours, setCustomHours] = useState<string | null>(
    valueMinutes > 0 && !initialIsPreset ? String(Math.floor(valueMinutes / 60)) : null,
  );
  const [customMinutes, setCustomMinutes] = useState<string | null>(
    valueMinutes > 0 && !initialIsPreset ? String(valueMinutes % 60) : null,
  );
  const customDurationSummary = customHours !== null && customMinutes !== null
    ? [
        Number(customHours) > 0
          ? `${customHours} ${Number(customHours) === 1 ? 'hour' : 'hours'}`
          : null,
        Number(customMinutes) > 0 ? `${customMinutes} min` : null,
      ].filter(Boolean).join(' ')
    : null;

  const updateCustomDuration = (hours: string | null, minutes: string | null) => {
    setCustomHours(hours);
    setCustomMinutes(minutes);
    if (hours === null || minutes === null) {
      onChange(0);
      return;
    }

    const totalMinutes = (Number(hours) * 60) + Number(minutes);
    onChange(totalMinutes >= 5 && totalMinutes <= 480 ? totalMinutes : 0);
  };

  return (
    <div className="contents">
      <SearchSelect
        label={t('booking.durationLabel')}
        requiredMark
        value={selection}
        onChange={(value) => {
          setSelection(value);
          if (value === 'custom') {
            setCustomHours(null);
            setCustomMinutes(null);
            onChange(0);
            return;
          }
          onChange(Number(value));
        }}
        options={[
          { value: '5', label: '5 min' },
          { value: '10', label: '10 min' },
          { value: '15', label: '15 min' },
          { value: '30', label: '30 min' },
          { value: '60', label: '1 hour' },
          { value: 'custom', label: t('booking.durationCustom') },
        ]}
        placeholder={t('booking.durationPlaceholder')}
        searchable={false}
        triggerRole="combobox"
      />

      {selection === 'custom' && (
        <div
          className="col-span-2 rounded-xl border border-border bg-surface p-3.5"
          role="group"
          aria-labelledby={customDurationLabelId}
        >
          <div className="mb-3 flex min-h-6 items-center justify-between gap-3">
            <p id={customDurationLabelId} className="text-13 font-semibold text-text">
              {t('booking.durationCustomSetup')}
            </p>
            <span className={customDurationSummary
              ? 'rounded-full bg-primary-weak px-2.5 py-1 text-12 font-semibold text-primary'
              : 'text-12 text-text-3'}
            >
              {customDurationSummary ?? t('booking.durationChooseParts')}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <SearchSelect
              label={t('booking.durationHours')}
              value={customHours}
              onChange={(value) => updateCustomDuration(value, customMinutes)}
              options={Array.from({ length: 9 }, (_, hours) => ({
                value: String(hours),
                label: hours === 1 ? '1 hour' : `${hours} hours`,
                disabled: hours === 0 && customMinutes === '0',
              }))}
              placeholder={t('booking.durationHoursPlaceholder')}
              searchable={false}
              triggerRole="combobox"
              size="sm"
            />
            <SearchSelect
              label={t('booking.durationMinutes')}
              value={customMinutes}
              onChange={(value) => updateCustomDuration(customHours, value)}
              options={Array.from({ length: 12 }, (_, index) => {
                const minutes = index * 5;
                return {
                  value: String(minutes),
                  label: `${minutes} min`,
                  disabled: (minutes === 0 && customHours === '0') || (minutes > 0 && customHours === '8'),
                };
              })}
              placeholder={t('booking.durationMinutesPlaceholder')}
              searchable={false}
              triggerRole="combobox"
              size="sm"
            />
          </div>
        </div>
      )}
    </div>
  );
}
